/**
 * Inhaltsverzeichnis
 * 	1. Imports
 * 	2. Scroll
 * 		2.1 Properties
 * 		2.2 Constructor
 * 		2.3 Constructor
 * 		2.4 Get Duration
 * 		2.5 Get Element Coordinates
 * 		2.6 To
 * 		2.7 To Top
 * 		2.8 Init
 * 
 */


// ══════════════════════════════════════════════════
// MARK: 0. Setup
// ──────────────────────────────────────────────────
// #═#═#═#═#═# 0.1 Imports #═#═#═#═#═#
import {header, root, adminbar} from '../../module/element.ts';
import {is} from '../../module/type.ts';
const	$	=	jQuery;


// #═#═#═#═#═# 0.2 Types #═#═#═#═#═#
export namespace Scroll {
	export interface Props {

	}
	export type Coordinates	=	{
		x: number;
		y: number;
	};
	export type Options = {
		duration?: number;
		easing?: 'swing' | 'linear' | 'easein' | 'easeout' | 'easeinout' | 'easeInBounce' | 'easeOutBounce' | 'easeInOutBounce';
		queue?: boolean;
		offset?: {
			x?: number;
			y?: number;
			header?: boolean;
			adminbar?: boolean;
		};
		element?: HTMLElement;
		instant?: boolean; // If true, the scroll will not be animated
	};
	export type Target	=	number | string | HTMLElement | Partial<Coordinates & Options>;
}

type Prettify<T> = {
  [K in keyof T]: T[K];
} & {};


// #═#═#═#═#═# 0.3 Declaration #═#═#═#═#═#
declare global {
	interface Number {
		/**
		 * Checks if the number is within the given range
		 * @param		min		The minimum value
		 * @param		max		The maximum value
		 * @returns		True if the number is within the range, false otherwise
		 */
		inRange(min: number, max: number): boolean;
		/**
		 * Clamps the number to the given range
		 * @param		min		The minimum value
		 * @param		max		The maximum value
		 * @returns		The clamped number
		 */
		clamp(min: number, max: number): number;
	}
}


// ══════════════════════════════════════════════════
// MARK: 2. Scroll Controller
// ──────────────────────────────────────────────────
/**
 * This class handles all Scroll related tasks
 * @since		6.0.0.6
 * @version		1.0
 * @author		Elko Keil
 * @package		scroll
 */
export default class Scroll {
	// #=#=#=#=#=# 2.1 Properties #=#=#=#=#=#
		// #==== Initialised	| Static ====#
	/**	Debug Boolean */
	public static	debug:boolean				= false;
	/**	The minimum Scroll position */
	public static	pageTop:number				= 0;
	/** Subtract the height of the header */
	public static	respectHeaderByDefault:boolean		= true;
	/** Subtract the height of the adminbar */
	public static	respectAdminbarByDefault:boolean	= true;
	/** Append Hash to History */
	public static	appendHashToHistory:boolean	= true;
	/**	The maximum Scroll position */
	private static	minScrollY:number			= 0;
	/**	The maximum Scroll position */
	private static	maxScrollY:number			= root.scrollHeight - window.innerHeight;
	/** Lock the horizontal Scroll */
	public static	lockHorizontalScroll:boolean	= true;
	/**	Base duration of an animation in ms */
	private static	baseDuration:number			= 300;
	/**	Speed Index of the Animation */
	private static	speed:number				= 1;
	/**	Screen Height */
	private static	screenHeight:number			= window.innerHeight;


	// #════ 2.2 Constructor ════#
	private constructor() {
		// NON CONSTROCTING
	}


	// #═#═#═#═#═# 2.3 Get Distance #═#═#═#═#═#
	/**
	 * Retrieves the relative distance between the current scroll position and the given coordinates
	 * @param		x		X destination
	 * @param		y		Y destination
	 * @returns		Distance to the given coordinates
	 */
	private static getDistance(
		x:number,
		y:number
	): {x:number, y:number} {
		return {
			x: Math.abs(x - root.scrollLeft),
			y: Math.abs(y - root.scrollTop)
		}
	}


	// #═#═#═#═#═# 2.4 Get Duration #═#═#═#═#═#
	/**
	 * Retrieves the duration of the scroll animation based on the given position difference to the target
	 * @param		delta		The distance to the target
	 * @returns		The duration of the scroll animation in ms
	 */
	private static getDuration(
		delta: {x:number, y:number}
	): number {
		// #==== Calculate Delta ====#
		const duration	= this.baseDuration * delta.y / this.screenHeight;


		// #==== Set Minimum ====#
		if(duration < this.baseDuration) return this.baseDuration;


		// #==== Return ====#
		return duration;
	}


	// #═#═#═#═#═# 2.5 Get Element Coordinates #═#═#═#═#═#
	/**
	 * Retrieves the coordinates of an Element relative to the page
	 * @param		element		The Element to retrieve the coordinates from
	 * @returns		The coordinates of the Element
	 */
	private static getElementCoordinates(
		element:HTMLElement
	): {x:number, y:number} {
		// #==== Guard ====#
		if(!is.element(element)) {
			throw new Error('Scroll.to: The target needs to be a Number, HTMLElement or Object.');
		}


		// #==== Get Coords ====#
		const {left, top}	= element.getBoundingClientRect();
		return {
			x: left + window.scrollX,
			y: top + window.scrollY
		};
	}


	// #═#═#═#═#═# 2.6 To #═#═#═#═#═#
	/**
	 * Scrolls to given coordinates or Element
	 * @param		target		The target to scroll to
	 * @param		x			The x coordinate to scroll to
	 */
	public static to(y: number, x: number): void;	// Coordinates
	public static to(target:string): void;			// Selector
	public static to(target:Scroll.Target): void;	// Target Object
	public static to(
		target:Scroll.Target,
		x?:number
	): void {
		// #════ Identefy Type ════#
		switch(true) {
			case is.number(target):
				// +──── Convert Type ────+
				target	=	{y: target as number};

				// +──── Set Y ────+
				if(!!x) {
					target.x	=	x;
				}
				break;

			case is.string(target):
				// +──── Convert Type ────+
				const element	=	document.querySelector(target as string);
				if(!element) {
					throw new Error(`Scroll.to: The target "${target}" could not be found.`);
				}
				target = element as HTMLElement;
				// No-break;

			case is.element(target):
				// +──── Convert Type ────+
				target		=	Object.assign({
					element: target as HTMLElement,
				}, Scroll.getElementCoordinates(target as HTMLElement));
				break;

			case is.object(target):
				// +──── Guard ────+
				target = target as Partial<Scroll.Coordinates &Scroll.Options>;
				if(!target.x && !target.y && !is.element(target.element)) {
					throw new Error('Scroll.to: The target object needs to have at least one coordinate.');
				}


				// +──── Convert Type ────+
				if(target.element && is.element(target.element)) {
					const coordinates	=	Scroll.getElementCoordinates(target.element);
					target				=	Object.assign(target, coordinates);
				}
				break;

			default:
				// +---- Fail ----+
				throw new Error('Scroll.to: The target needs to be a Number, HTMLElement or Object.');
		}

		// Redefine Type
		target = target as Scroll.Coordinates & Partial<Scroll.Options>;


		// #════ Animation Options ════#
		// Set up the default animation options
		const options:Scroll.Options	= {
			duration: 0,
			easing: 'swing',
			queue: false,
		};


		// #════ Calculate Delta ════#
		// If smooth scrolling is enabled, calculate a smoother scroll duration
		if(!target.instant) {
			const delta			= this.getDistance(target.x as number, target.y as number);
			target.duration		??= this.getDuration(delta);

			options.duration	= target.duration / this.speed;
		}


		// #════ Target Destination ════#
		// Animate the windows scroll position
		const destination: Record<string, number>	=	{};
		if(!this.lockHorizontalScroll && target.x) destination.scrollLeft	=	target.x;
		if(target.y) destination.scrollTop	=	target.y;


		// #════ Offsets ════#
			// +──── Elements ────+
		if(header && (target.offset?.header ?? this.respectHeaderByDefault)) {
			const headerHeight = header.getBoundingClientRect().height;
			destination.scrollTop	-=	headerHeight;
		}

		if(adminbar && (target.offset?.adminbar ?? this.respectAdminbarByDefault)) {
			const adminbarHeight = adminbar.getBoundingClientRect().height;
			destination.scrollTop	-=	adminbarHeight;
		}

			// +──── Calculations ────+
		if(target.x && target.offset?.x) destination.scrollLeft	+=	target.offset?.x;
		if(target.y && target.offset?.y) destination.scrollTop	+=	target.offset?.y;


		// #════ Validate Range ════#
		// Adjust the scroll position to the minimum and maximum scroll position
		if(Boolean(destination.scrollLeft) && destination.scrollLeft < 0) destination.scrollLeft = 0;
		if(Boolean(destination.scrollTop) && !destination.scrollTop.inRange(this.minScrollY, this.maxScrollY)) {
			destination.scrollTop		=	destination.scrollTop.clamp(this.minScrollY, this.maxScrollY);
		}


		// #════ Before Scrolling ════#
		// Append the abort scrolling event listeners to prevent scrolling while the animation is running
		document.addEventListener('wheel', Scroll.abortScrollAnimation, {once: true});
		document.addEventListener('touchstart', Scroll.abortScrollAnimation, {once: true});


		// Append Hash to location History
		if(this.appendHashToHistory && is.element(target.element)) {
			const element = target.element as HTMLElement;

			this._doAppendHashHistory(element);
		}


		// #════ Run Animation ════#
		$(root).stop().animate(destination, options);


		// #════ After Animation Complete ════#
		$(root).promise().done(() => {
			// +──── Remove EventListener ────+
			document.removeEventListener('wheel', Scroll.abortScrollAnimation);
			document.removeEventListener('touchstart', Scroll.abortScrollAnimation);
		});
	}


	// #═#═#═#═#═# 2.7 To Top #═#═#═#═#═#
	/**
	 * Scrolls to the top of the page
	 */
	public static toTop() {
		Scroll.to({
			y: this.pageTop
		});
	}


	// #═#═#═#═#═# 2.8 Abort Scrolling #═#═#═#═#═#
	/**
	 * Aborts the current scrolling animation when the the scrollwheel is used or a touch event is triggered
	 * @param		event		The event that triggered the abort
	 * @returns		True if the scrolling was aborted, false otherwise
	 */
	public static abortScrollAnimation = (
	): boolean => {
		// #════ Check if Scrolling is Active ════#
		if(!$(root).is(':animated')) {
			if(this.debug) console.warn('Scroll.abortScrolling: No scrolling animation is currently active.');
			return false;
		}


		// #════ Abort Scrolling ════#
		// Stop the current scrolling animation
		try {
			$(root).stop(true, false);
			return true;
		} catch(error) {
			if(this.debug) console.error('Scroll.abortScrolling: An error occurred while stopping the scrolling animation.', error);
			return false;
		}
	}


	// #═#═#═#═#═# 2.9 Append Anchor Hash to history #═#═#═#═#═#
	/**
	 * Appends the anchor hash to the history
	 * @param		target		The anchor element to append the hash from
	 * @returns		{void}
	 */
	private static _doAppendHashHistory(
		target: string|HTMLElement
	): void {
		// #════ Validate ════#
		if(typeof target === 'string') {
			if(target.startsWith('#')) {
				// If the anchor is a string, remove the hash
				target	=	target.substring(1);
			}

			// If the anchor is a string, try to get the element
			const maybeTarget	=	document.getElementById(target);
			if(!maybeTarget) {
				if(this.debug) console.warn(`Scroll.to: The anchor "#${target}" could not be found.`);
				return;
			}

			target	=	maybeTarget;
		}


		// #════ Guard ════#
		if(!target.id) {
			return;
		}
		
		if(history?.pushState) {
			history.pushState(null, '', `#${target.id}`);
		} else {
			window.location.hash	= target.id;
		}
	}


	// #═#═#═#═#═# 2.8 Is Same Site Anchor #═#═#═#═#═#
	/**
	 * Determines if the given anchor is only to scroll within the same page
	 * @param anchor The anchor element to check
	 * @returns 
	 */
	private static _isSameSiteAnchor(
		anchor: HTMLAnchorElement|null
	): boolean {
		// #════ Guard ════#
		if(!anchor) {
			if(this.debug) console.warn('Scroll.to: The anchor element is null or undefined.');
			return false;
		}


		// #════ Variables ════#
		// Get the current location info
		const currentPath		= window.location.pathname;
		const currentHostname	= window.location.hostname;


		// #════ Check Cases ════#
		if(!anchor.hash) {
			// If the anchor has no hash, cant be anchor link
			return false;
		}

		
		// Host or Path does not match
		if(
			(!is.empty(anchor.hostname) && anchor.hostname !== currentHostname) ||
			(!is.empty(anchor.pathname) && anchor.pathname !== currentPath)
		) {
			return false;
		}

		// If the anchor has a hash and stays within the same page, it is a same site anchor
		return true;
	}


	// #═#═#═#═#═# 2.8 with Pointer Interaction #═#═#═#═#═#
	private static _pointerInteraction = (event: MouseEvent) => {
		// #════ Guard ════#
		let anchor	= (event.target as HTMLElement).closest('a');
		if(!anchor) return;


		// #════ Check for Hash ════#
		if(this._isSameSiteAnchor(anchor)) {
			// Prevent Default
			event.preventDefault();

			// Get Target
			const target	=	document.querySelector(anchor.hash) as HTMLElement|null;

			if(!target) {
				if(this.debug) console.warn(`Scroll.to: The target "${anchor.hash}" could not be found.`);
				return;
			}

			// Scroll to Target
			this.to(target);
		}
	};


	// #═#═#═#═#═# 2.9 with Hash Change #═#═#═#═#═#
	private static _hashChange = (event: HashChangeEvent) => {
		// #════ Guard ════#
		if(!event.newURL) return;


		// #════ Get Hash ════#
		const hash = event.newURL.split('#')[1];
		if(!hash) {
			return;
		}


		// #════ Get Target ════#
		const target	=	document.getElementById(hash);
		if(!target) {
			if(this.debug) console.warn(`Scroll.to: The target "${hash}" could not be found.`);
			return;
		}


		// #════ Scroll to Target ════#
		try {
			this.to({
				element: target,
				instant: true,
			});
		} catch(error) {
			if(this.debug) console.error(`Scroll.to: An error occurred while scrolling to the target "${hash}".`, error);
		}
	}


	// #═#═#═#═#═# 2.9 Init #═#═#═#═#═#
	/**
	 * Initialises static values and events within the class
	 */
	public static __init(
	): void {
		// #════ On load ════#
		// Scroll to the hash target when the page loads
		if(window.location.hash) {
			const target	=	document.querySelector(window.location.hash);

			if(target) {
				try {
					this.to({
						element: target as HTMLElement,
						instant: true,
					});
				} catch(error) {
					if(this.debug) console.error(`Scroll.to: An error occurred while scrolling to the target "${window.location.hash}".`, error);
				}
			}
		}


		// #════ Button ════#
		document.addEventListener('click', this._pointerInteraction);


		// #════ Hash Change ════#
		window.addEventListener('hashchange', this._hashChange);
	}
}